import type { LocalDate } from "@js-joda/core";
import moment from "moment-timezone";
import { ColorV2 } from "config/colors";
import { ApplicationResolution, ApplicationStatus } from "model/Application";
import type { ApplicationGig } from "model/Application";
import { GigType } from "model/Gig";
import type { CompanyGigShift } from "model/Gig";
import { toStockholmLocalDateFromUtcIso } from "web-apps/company/components/schedule/utils/schedule.dates";
import type {
  ApplicationFilter,
  ApplicationListView,
} from "./applicationList.filter.logic";

interface StaffingApplication {
  readonly gig: {
    readonly endTime?: string;
    readonly id?: number;
    readonly shifts?: ApplicationGig["shifts"];
    readonly startTime?: string;
    readonly type: GigType;
  };
}

interface GroupableStaffingApplication extends StaffingApplication {
  readonly gig: { readonly id: number; readonly type: GigType };
}

export interface StaffingShiftGroup<T extends GroupableStaffingApplication> {
  readonly applications: T[];
  readonly gigId: number;
}

interface ResolveStaffingPageParams<T extends StaffingApplication> {
  readonly applications: readonly T[];
  readonly currentPage: number;
  readonly pageSize: number;
}

interface FilterApplicationsByDateParams<T extends StaffingApplication> {
  readonly applications: readonly T[];
  readonly selectedDate: LocalDate | null;
}

interface ApplicationDateParams {
  readonly application: StaffingApplication;
  readonly selectedDate: LocalDate | null;
}

interface FilterApplicationsByViewParams<T extends StaffingApplication> {
  readonly applications: readonly T[];
  readonly now: number;
  readonly view: ApplicationListView;
}

interface SortApplicationsByDateParams<T extends StaffingApplication> {
  readonly applications: readonly T[];
  readonly view: ApplicationListView;
}

interface DatedApplication<T extends StaffingApplication> {
  readonly application: T;
  readonly originalIndex: number;
  readonly startTime: number;
}

export interface StaffingPage<T> {
  readonly complete: boolean;
  readonly nextPage: number;
  readonly visibleApplications: T[];
}

interface ContinueStaffingPageFetchParams {
  readonly complete: boolean;
  readonly currentPage: number;
  readonly nextPage: number;
  readonly targetApplicationCount: number;
  readonly visibleApplicationCount: number;
}

interface StatusApplication {
  readonly resolution: ApplicationResolution;
  readonly status: ApplicationStatus;
}

interface ApplicationFilterMatchParams {
  readonly application: StatusApplication;
  readonly filter: ApplicationFilter;
}

export interface ApplicationStatusChip {
  readonly backgroundColor: ColorV2;
  readonly labelKey: string;
  readonly textColor: ColorV2;
}

export type ApplicationShiftType =
  | { type: "public"; iconName: "v2/globe" }
  | { type: "team"; iconName: "v2/team" }
  | { type: "unsupported" };

export interface ApplicationShiftSchedule {
  readonly dates: string;
  readonly shiftLabels: readonly string[];
  readonly timeSpans: readonly string[];
}

interface ApplicationShiftScheduleParams {
  readonly gig: Pick<ApplicationGig, "endTime" | "shifts" | "startTime">;
  readonly locale: string;
}

interface GigShiftTimes {
  readonly endTime: string;
  readonly startTime: string;
  readonly tooltipDate: string;
}

export function toApplicationShiftSchedule({
  gig,
  locale,
}: ApplicationShiftScheduleParams): ApplicationShiftSchedule {
  const shifts = gig.shifts?.length ? gig.shifts : [gig];
  const formattedShifts = shifts
    .map((shift) => formatGigShift({ shift, locale }))
    .filter(
      (shift): shift is GigShiftTimes & { readonly date: string } =>
        shift !== null,
    );
  const displayShifts =
    formattedShifts.length > 0
      ? formattedShifts
      : [formatGigShift({ shift: gig, locale })].filter(
          (shift): shift is GigShiftTimes & { readonly date: string } =>
            shift !== null,
        );

  return {
    dates: toDateRange(displayShifts.map((shift) => shift.date)),
    shiftLabels: displayShifts.map(
      ({ tooltipDate, startTime, endTime }) =>
        `${tooltipDate} ${startTime}–${endTime}`,
    ),
    timeSpans: unique(
      displayShifts.map(({ startTime, endTime }) => `${startTime}–${endTime}`),
    ),
  };
}

function toDateRange(dates: readonly string[]): string {
  const uniqueDates = unique(dates);
  const firstDate = uniqueDates[0];
  const lastDate = uniqueDates[uniqueDates.length - 1];
  if (!firstDate) return "";
  return firstDate === lastDate ? firstDate : `${firstDate} – ${lastDate}`;
}

interface FormatGigShiftParams {
  readonly locale: string;
  readonly shift: Pick<CompanyGigShift, "endTime" | "startTime">;
}

function formatGigShift({
  locale,
  shift,
}: FormatGigShiftParams): (GigShiftTimes & { readonly date: string }) | null {
  const start = moment
    .tz(shift.startTime, moment.ISO_8601, true, "Europe/Stockholm")
    .locale(locale);
  const end = moment
    .tz(shift.endTime, moment.ISO_8601, true, "Europe/Stockholm")
    .locale(locale);
  if (!start.isValid() || !end.isValid()) return null;
  return {
    date: start.format("D/M"),
    tooltipDate: start.format("D MMMM"),
    startTime: start.format("HH:mm"),
    endTime: end.format("HH:mm"),
  };
}

function unique(values: readonly string[]): string[] {
  return Array.from(new Set(values));
}

export function toApplicationShiftType(gigType: GigType): ApplicationShiftType {
  switch (gigType) {
    case GigType.PUBLIC:
      return { type: "public", iconName: "v2/globe" };
    case GigType.WORKPOOL:
      return { type: "team", iconName: "v2/team" };
    case GigType.AUTOGENERATED:
    case GigType.HIDDEN:
    case GigType.SOLO:
    case GigType.STAFFING:
    case GigType.UNIT:
    case GigType.WORKGROUP:
      return { type: "unsupported" };
    default:
      return { type: "unsupported" };
  }
}

export function isVisibleStaffingApplication(
  application: StaffingApplication,
): boolean {
  return toApplicationShiftType(application.gig.type).type !== "unsupported";
}

export function shouldShowApplicationContract(gigType: GigType): boolean {
  return gigType === GigType.PUBLIC;
}

export function groupStaffingApplicationsByShift<
  T extends GroupableStaffingApplication,
>(applications: readonly T[]): StaffingShiftGroup<T>[] {
  const groups = new Map<number, T[]>();

  applications.forEach((application) => {
    const existingApplications = groups.get(application.gig.id);
    if (existingApplications) {
      existingApplications.push(application);
      return;
    }
    groups.set(application.gig.id, [application]);
  });

  return Array.from(groups, ([gigId, groupedApplications]) => ({
    applications: groupedApplications,
    gigId,
  }));
}

export function resolveStaffingPage<T extends StaffingApplication>(
  params: ResolveStaffingPageParams<T>,
): StaffingPage<T> {
  const complete = params.applications.length < params.pageSize;
  return {
    complete,
    nextPage: complete ? params.currentPage : params.currentPage + 1,
    visibleApplications: params.applications.filter(
      isVisibleStaffingApplication,
    ),
  };
}

export function shouldContinueStaffingPageFetch(
  params: ContinueStaffingPageFetchParams,
): boolean {
  return (
    params.visibleApplicationCount < params.targetApplicationCount &&
    !params.complete &&
    params.nextPage > params.currentPage
  );
}

export function filterApplicationsByDate<T extends StaffingApplication>({
  applications,
  selectedDate,
}: FilterApplicationsByDateParams<T>): T[] {
  if (!selectedDate) return [...applications];
  return applications.filter((application) =>
    isApplicationOnDate({ application, selectedDate }),
  );
}

export function filterApplicationsByView<T extends StaffingApplication>({
  applications,
  now,
  view,
}: FilterApplicationsByViewParams<T>): T[] {
  if (view === "All") return [...applications];
  return applications.filter(
    (application) => !hasApplicationGigPassed({ application, now }),
  );
}

export function sortApplicationsByDate<T extends StaffingApplication>(
  params: SortApplicationsByDateParams<T>,
): T[] {
  const direction = params.view === "All" ? -1 : 1;
  return params.applications
    .map<DatedApplication<T>>((application, originalIndex) => ({
      application,
      originalIndex,
      startTime: getFirstGigShiftStart(application),
    }))
    .sort((first, second) => {
      const dateOrder = (first.startTime - second.startTime) * direction;
      return dateOrder || first.originalIndex - second.originalIndex;
    })
    .map((item) => item.application);
}

interface HasApplicationGigPassedParams {
  readonly application: StaffingApplication;
  readonly now: number;
}

function hasApplicationGigPassed({
  application,
  now,
}: HasApplicationGigPassedParams): boolean {
  const lastShiftEnd = getLastGigShiftEnd(application);
  return Number.isFinite(lastShiftEnd) && lastShiftEnd <= now;
}

function getFirstGigShiftStart(application: StaffingApplication): number {
  const firstShift = application.gig.shifts?.[0];
  return Date.parse(firstShift?.startTime ?? application.gig.startTime ?? "");
}

function getLastGigShiftEnd(application: StaffingApplication): number {
  const shifts = application.gig.shifts;
  const lastShift = shifts?.[shifts.length - 1];
  const endTime = Date.parse(
    lastShift?.endTime ?? application.gig.endTime ?? "",
  );
  return Number.isFinite(endTime) ? endTime : Number.POSITIVE_INFINITY;
}

function isApplicationOnDate({
  application,
  selectedDate,
}: ApplicationDateParams): boolean {
  if (!selectedDate) return true;
  const startTimes = application.gig.shifts?.length
    ? application.gig.shifts.map((shift) => shift.startTime)
    : [application.gig.startTime];
  return startTimes.some((startTime) =>
    toStockholmLocalDateFromUtcIso(startTime)?.equals(selectedDate),
  );
}

export function toApplicationStatusChip(
  application: StatusApplication,
): ApplicationStatusChip | null {
  if (application.status === ApplicationStatus.OFFERED) {
    return {
      backgroundColor: ColorV2.OfferedYellow,
      labelKey: "GigsCompany.Offered",
      textColor: ColorV2.Blue1,
    };
  }

  switch (application.resolution) {
    case ApplicationResolution.ACCEPTED:
      return {
        backgroundColor: ColorV2.PositiveGreenLight,
        labelKey: "ApplicationsCompany.Hired",
        textColor: ColorV2.SimflexityGreen,
      };
    case ApplicationResolution.REJECTED:
    case ApplicationResolution.DECLINED:
      return {
        backgroundColor: ColorV2.NegativeRedLight,
        labelKey: "ApplicationsCompany.NotSelected",
        textColor: ColorV2.NegativeRed,
      };
    case ApplicationResolution.WITHDRAWN:
      return {
        backgroundColor: ColorV2.WithdrawnPink,
        labelKey: "ApplicationsCompany.NotSelected",
        textColor: ColorV2.Blue1,
      };
    case ApplicationResolution.UNDEFINED:
      return null;
    default:
      return null;
  }
}

export function matchesApplicationFilter({
  application,
  filter,
}: ApplicationFilterMatchParams): boolean {
  switch (filter) {
    case "All":
      return true;
    case "Accepted":
      return (
        application.status !== ApplicationStatus.OFFERED &&
        application.resolution === ApplicationResolution.ACCEPTED
      );
    case "Rejected":
      return (
        application.status !== ApplicationStatus.OFFERED &&
        application.resolution === ApplicationResolution.REJECTED
      );
    case "Offered":
      return application.status === ApplicationStatus.OFFERED;
    case "Withdrawn":
      return (
        application.status !== ApplicationStatus.OFFERED &&
        application.resolution === ApplicationResolution.WITHDRAWN
      );
    default:
      return false;
  }
}
